import React, { useEffect, useRef, useState } from 'react';
import SvgDownArrowSm from '../Global/ShowMoreArrow/SvgDownArrowSm';
import type { NormalizedContentExplorerSlide } from './contentExplorerUtils';

type Styles = { readonly [key: string]: string };

const CLOSE_MS = 600;

function MobileAccordionPanelMotion({
    motionStyles,
    open,
    onClosed,
    children,
}: {
    motionStyles: Styles;
    open: boolean;
    onClosed?: () => void;
    children: React.ReactNode;
}): React.ReactElement {
    const [heightOpen, setHeightOpen] = useState(false);
    const closeNotifiedRef = useRef(false);
    const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const invokeClosed = () => {
        if (closeNotifiedRef.current) return;
        closeNotifiedRef.current = true;
        onClosed?.();
    };

    useEffect(() => {
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
        if (open) {
            closeNotifiedRef.current = false;
            setHeightOpen(false);
            const id = requestAnimationFrame(() => {
                requestAnimationFrame(() => setHeightOpen(true));
            });
            return () => cancelAnimationFrame(id);
        }
        setHeightOpen(false);
        fallbackTimerRef.current = setTimeout(() => {
            fallbackTimerRef.current = null;
            invokeClosed();
        }, CLOSE_MS);
        return () => {
            if (fallbackTimerRef.current) {
                clearTimeout(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
            }
        };
    }, [open]);

    const handleOuterTransitionEnd = (
        e: React.TransitionEvent<HTMLDivElement>
    ) => {
        if (e.target !== e.currentTarget) return;
        if (open || heightOpen) return;
        if (
            e.propertyName !== 'grid-template-rows' &&
            e.propertyName !== 'grid-template-columns'
        ) {
            return;
        }
        if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
        invokeClosed();
    };

    return (
        <div
            className={`${motionStyles.mobileAccordionPanelOuter} ${
                heightOpen ? motionStyles.mobileAccordionPanelOuterOpen : ''
            }`}
            onTransitionEnd={handleOuterTransitionEnd}
        >
            <div className={motionStyles.mobileAccordionPanelInner}>
                <div
                    className={`${motionStyles.mobileAccordionPanel} ${
                        heightOpen
                            ? motionStyles.mobileAccordionPanelVisible
                            : ''
                    }`}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

type ContentExplorerMobileAccordionProps = {
    slides: NormalizedContentExplorerSlide[];
    activeIndex: number;
    panelExpanded: boolean;
    closeAnimatingIndex: number | null;
    onCloseAnimationComplete: () => void;
    baseId: string;
    styles: Styles;
    onRowClick: (index: number) => void;
    panelId: string;
    children: React.ReactNode;
};

export function ContentExplorerMobileAccordion({
    slides,
    activeIndex,
    panelExpanded,
    closeAnimatingIndex,
    onCloseAnimationComplete,
    baseId,
    styles,
    onRowClick,
    panelId,
    children,
}: ContentExplorerMobileAccordionProps): React.ReactElement | null {
    if (slides.length < 2) {
        return null;
    }

    return (
        <div className={styles.mobileAccordion}>
            {slides.map((slide, index) => {
                const expanded = panelExpanded && index === activeIndex;
                const showPanel =
                    expanded ||
                    (closeAnimatingIndex === index && !panelExpanded);
                const motionOpen = expanded;
                const triggerId = `${baseId}-mobile-accordion-${index}`;
                return (
                    <div key={slide.id} className={styles.mobileAccordionItem}>
                        <button
                            type="button"
                            id={triggerId}
                            className={styles.mobileAccordionTrigger}
                            aria-expanded={expanded}
                            aria-controls={showPanel ? panelId : undefined}
                            onClick={() => onRowClick(index)}
                        >
                            <span className={styles.mobileAccordionLabel}>
                                {slide.navLabel.toUpperCase()}
                            </span>
                            <span
                                className={`${styles.mobileAccordionChevron} ${
                                    expanded
                                        ? styles.mobileAccordionChevronOpen
                                        : ''
                                }`}
                                aria-hidden
                            >
                                <SvgDownArrowSm
                                    strokeColor="#ffffff"
                                    transform="0"
                                />
                            </span>
                        </button>
                        {showPanel ? (
                            <MobileAccordionPanelMotion
                                key={`${slide.id}-panel`}
                                motionStyles={styles}
                                open={motionOpen}
                                onClosed={
                                    closeAnimatingIndex === index
                                        ? onCloseAnimationComplete
                                        : undefined
                                }
                            >
                                {children}
                            </MobileAccordionPanelMotion>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
